// @ts-check
/** @typedef {{x:number,y:number,age:number,life:number,kind:string,text:string,seed:number}} Burst */
/** Bounded world-space feedback. No camera shake or deformation of QP's letters.
 * @param {Document} root
 */
export function createFeedback(root) {
  const canvas = root.createElement('canvas'); canvas.id = 'game-fx'; canvas.setAttribute('aria-hidden', 'true'); root.body.append(canvas);
  const c = canvas.getContext('2d');
  const style = window.getComputedStyle(root.documentElement);
  const color = (/** @type {string} */ name) => style.getPropertyValue(`--color-${name}`).trim();
  const colors = { cyan: color('glow-cyan'), gold: color('reward-gold'), danger: color('danger'), violet: color('space-violet'), light: color('surface-12'), dark: color('space-void') };
  /** @type {Burst[]} */ let bursts = [];
  let dustClock = 0, serial = 0;
  /** @param {number} x @param {number} y @param {string} kind @param {number} life @param {string} [text] */
  function add(x, y, kind, life, text = '') { if (bursts.length >= 64) bursts.shift(); bursts.push({ x, y, kind, life, text, age: 0, seed: serial++ }); }
  return {
    /** @param {import('../game/types.js').GameEvent} event */
    accept(event) {
      if (event.type === 'restarted') bursts = [];
      if (event.type === 'scored') add(event.x, event.y + 18, 'score', 1.1, `+${event.amount}`);
      if (event.type === 'enemy-defeated' || event.type === 'pulse-impact') add(event.x, event.y + 12, 'impact', .5);
      if (event.type === 'damaged') add(event.x + 12, event.y + 24, 'damage', .65);
      if (event.type === 'double-jump') add(event.x + 12, event.y, 'ring', .5);
      if (event.type === 'land') add(event.x + 12, event.y, 'land', .4);
      if (event.type === 'boss-hit') add(event.x, event.y - 28, 'impact', .8);
      if (event.type === 'boss-defeated') add(event.x, event.y - 24, 'restore', 2.4);
      if (event.type === 'checkpoint' || event.type === 'pickup') add(event.x + 12, event.y + 22, 'ring', .8);
    },
    /** @param {number} dt @param {import('../game/session.js').Session} session */
    step(dt, session) {
      for (const b of bursts) b.age += dt;
      bursts = bursts.filter(b => b.age < b.life); dustClock += dt;
      if (dustClock > .08) {
        dustClock = 0;
        const p = session.player;
        if (session.mode === 'level' && p.grounded && Math.abs(p.vx) > 220 && !session.transfer) add(p.x + 10, p.y + 2, 'dust', .28);
      }
    },
    /** @param {number} camera @param {{width:number,height:number,floor:number,scale:number}} view @param {boolean} reduced @param {boolean} visible */
    render(camera, view, reduced, visible) {
      if (!c) return;
      canvas.hidden = !visible;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      if (canvas.width !== Math.round(view.width * ratio) || canvas.height !== Math.round(view.height * ratio)) { canvas.width = Math.round(view.width * ratio); canvas.height = Math.round(view.height * ratio); }
      c.setTransform(ratio, 0, 0, ratio, 0, 0); c.clearRect(0, 0, view.width, view.height);
      if (!visible) return;
      for (const b of bursts) {
        const x = (b.x - camera) * view.scale, y = view.floor - b.y * view.scale, t = b.age / b.life;
        if (x < -200 || x > view.width + 200) continue;
        c.save(); c.globalAlpha = Math.min(1, (1 - t) * 2);
        const ink = b.kind === 'score' ? colors.gold : b.kind === 'damage' ? colors.danger : colors.cyan;
        c.fillStyle = c.strokeStyle = ink;
        if (b.kind === 'score') {
          c.font = `700 ${Math.max(13, Math.min(20, view.scale * 10))}px ui-monospace, monospace`; c.textAlign = 'center';
          c.shadowColor = colors.dark; c.shadowBlur = 6;
          c.fillText(b.text, x, y - (reduced ? 0 : t * 32));
        } else if (!reduced) {
          c.lineWidth = b.kind === 'restore' ? 2 : 1;
          if (b.kind === 'ring' || b.kind === 'restore') {
            const radius = (b.kind === 'restore' ? 160 : 35) * t * view.scale;
            c.beginPath(); c.ellipse(x, y, Math.max(1, radius), Math.max(1, radius * .5), 0, 0, Math.PI * 2); c.stroke();
          }
          const count = b.kind === 'restore' ? 28 : b.kind === 'dust' ? 3 : 10;
          for (let i = 0; i < count; i++) {
            const angle = i * 2.399 + b.seed, distance = b.age * (b.kind === 'restore' ? 120 : b.kind === 'dust' ? 18 : 52) * view.scale;
            const dx = Math.cos(angle) * distance, dy = Math.sin(angle) * distance - (b.kind === 'land' || b.kind === 'dust' ? 10 * Math.sin(Math.PI * t) : 0);
            const size = (b.kind === 'restore' ? 3 : 2) * view.scale * (1 - t * .5);
            c.globalAlpha = (1 - t) * (b.kind === 'dust' ? .35 : .8);
            c.fillRect(x + dx, y + dy, size, size * (i % 2 ? .4 : 1));
          }
        }
        c.restore();
      }
    },
    get active() { return bursts.length > 0; },
    get count() { return bursts.length; },
    dispose() { bursts = []; canvas.width = canvas.height = 1; canvas.remove(); },
  };
}
